import React from "react";
import {
    Chart as ChartJS,
    ArcElement,
    BarElement,
    CategoryScale,
    Legend,
    LineElement,
    LinearScale,
    PointElement,
    Tooltip,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';

import { convertIntToMoney } from "../../../helpers/conversions";
import { buildChartData } from "../helpers";

ChartJS.register(ArcElement, BarElement, CategoryScale, Legend, LineElement, LinearScale, PointElement, Tooltip);

// Escala do roxo da marca, do mais escuro ao mais claro. Uma métrica usa a
// primeira; várias caminham pela escala — nunca cores geradas na hora.
const SERIES_COLORS = ['#4D3DAB', '#7B6FCC', '#A89EDF', '#241D54', '#CCC6EC'];

const AXIS_COLOR = '#7A7499';
const GRID_COLOR = '#EEECF7';

// Nome de cliente inteiro no eixo obriga o Chart.js a girar e a ESCONDER
// rótulo (autoSkip); cortado, cabe em pé. O nome completo continua no tooltip,
// que lê o rótulo original e não passa por aqui.
const LABEL_MAX = 16;

const shortLabel = label => {
    const text = String(label ?? '');

    return text.length > LABEL_MAX ? `${text.slice(0, LABEL_MAX - 1).trimEnd()}…` : text;
};

export const ReportChart = ({ result, type }) => {
    const chart = buildChartData(result, type);

    if (!chart) return null;

    const money = value => convertIntToMoney(Math.round(value), true);
    const formatValue = value => chart.moneyScale ? money(value) : new Intl.NumberFormat('pt-BR').format(value);

    const datasets = chart.measures.map((measure, index) => ({
        label: measure.label,
        data: chart.values[index],
        backgroundColor: type === 'doughnut' ? SERIES_COLORS : SERIES_COLORS[index % SERIES_COLORS.length],
        borderColor: SERIES_COLORS[index % SERIES_COLORS.length],
        borderWidth: type === 'line' ? 2 : 0,
        borderRadius: type === 'bar' ? 4 : 0,
        pointRadius: type === 'line' ? 4 : undefined,
        tension: type === 'line' ? 0.25 : undefined,
        // Anel na cor da superfície separa as fatias sem inventar uma borda.
        borderAlign: type === 'doughnut' ? 'inner' : undefined,
    }));

    const data = { labels: chart.labels, datasets };

    const common = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            // Uma série só já é nomeada pelo título da coluna; legenda aí só
            // ocupa espaço.
            legend: {
                display: datasets.length > 1 || type === 'doughnut',
                position: type === 'doughnut' ? 'right' : 'top',
                labels: {
                    boxWidth: 12,
                    boxHeight: 12,
                    color: '#1A1530',
                    font: { size: 12 },
                    // Na rosca a legenda é que nomeia as fatias, então o corte
                    // vale aqui também.
                    generateLabels: chartInstance => ChartJS.defaults.plugins.legend.labels
                        .generateLabels(chartInstance)
                        .map(item => ({ ...item, text: shortLabel(item.text) })),
                },
            },
            tooltip: {
                callbacks: {
                    label: context => {
                        const label = type === 'doughnut' ? context.label : context.dataset.label;
                        const value = type === 'doughnut' ? context.parsed : context.parsed.y;
                        return `${label}: ${formatValue(value)}`;
                    },
                },
            },
        },
    };

    const cartesian = {
        ...common,
        scales: {
            x: {
                grid: { display: false },
                border: { color: GRID_COLOR },
                ticks: {
                    color: AXIS_COLOR,
                    font: { size: 11 },
                    autoSkip: false,
                    maxRotation: 45,
                    callback: function (value) { return shortLabel(this.getLabelForValue(value)); },
                },
            },
            y: {
                grid: { color: GRID_COLOR },
                border: { display: false },
                ticks: {
                    color: AXIS_COLOR,
                    font: { size: 11 },
                    callback: value => chart.moneyScale ? money(value) : value,
                },
            },
        },
    };

    const cut = chart.shown < chart.total;
    const count = value => new Intl.NumberFormat('pt-BR').format(value);

    return (
        <>
            <div className="report-chart">
                {type === 'bar' && <Bar data={data} options={cartesian} />}
                {type === 'line' && <Line data={data} options={cartesian} />}
                {type === 'doughnut' && <Doughnut data={data} options={common} />}
            </div>

            {cut &&
                <div className="report-chart-note">
                    {chart.ordinal
                        ? `Gráfico com ${count(chart.shown)} de ${count(chart.total)} períodos`
                        : `Gráfico com os ${count(chart.shown)} maiores por ${chart.measures[0].label.toLowerCase()}, de ${count(chart.total)}`
                    } — a tabela abaixo traz todos.
                </div>
            }
        </>
    );
};

export default ReportChart;
